import React, { useState, useRef, ChangeEvent, useEffect } from "react";
// For the lucide-react error, you need to install it:
// npm install lucide-react
// or import your icons from another source you have available

// If you can't install lucide-react, define simple icon components:
const UploadIcon = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const Camera = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const X = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Check = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const File = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const FileText = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const Image = (props: { size?: number; className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

// Define types for our file objects
interface FileObject {
  name: string;
  size: number;
  type: string;
}

// Define type for preview URLs
interface PreviewUrls {
  [key: string]: string;
}

export default function Upload(): JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<FileObject[]>([]);
  const [previewUrls, setPreviewUrls] = useState<PreviewUrls>({});
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanFeedback, setScanFeedback] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Convert FileList to array of FileObjects
      const fileArray: FileObject[] = Array.from(files).map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      }));

      setSelectedFiles((prev) => [...prev, ...fileArray]);

      // Create preview URLs for images
      Array.from(files).forEach((file) => {
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          setPreviewUrls((prev) => ({ ...prev, [file.name]: url }));
        }
      });
    }
  };

  const removeFile = (fileName: string): void => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));

    // Revoke object URL to free memory
    if (previewUrls[fileName]) {
      URL.revokeObjectURL(previewUrls[fileName]);
      setPreviewUrls((prev) => {
        const newUrls = { ...prev };
        delete newUrls[fileName];
        return newUrls;
      });
    }
  };

  const triggerFileInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileIcon = (file: FileObject): JSX.Element => {
    if (file.type.startsWith("image/")) {
      return previewUrls[file.name] ? (
        <div className="w-10 h-10 rounded overflow-hidden bg-blue-50">
          <img
            src={previewUrls[file.name]}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <Image className="text-blue-500" size={24} />
      );
    } else if (file.type === "application/pdf") {
      return <File className="text-red-500" size={24} />;
    } else if (file.type.includes("document") || file.type.includes("sheet")) {
      return <FileText className="text-green-500" size={24} />;
    } else {
      return <File className="text-gray-500" size={24} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  // Document scanner functionality
  const startDocumentScanner = async (): Promise<void> => {
    try {
      setIsScanning(true);
      setScanFeedback("Starting camera...");

      // Request camera access with high resolution for better document scanning
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Use back camera if available
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      mediaStreamRef.current = stream;

      // Display camera feed
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setScanFeedback("Position document in frame and hold steady");
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setScanFeedback("Camera access failed. Please check permissions.");
      setIsScanning(false);
    }
  };

  const captureDocument = (): void => {
    if (!videoRef.current || !canvasRef.current) return;

    setScanFeedback("Processing image...");

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to file
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Create a file from the blob
          const timestamp = new Date().getTime();
          const scannedFile: FileObject = {
            name: `scanned-doc-${timestamp}.jpg`,
            size: blob.size,
            type: "image/jpeg",
          };

          // Add to selected files
          setSelectedFiles((prev) => [...prev, scannedFile]);

          // Create preview URL
          const url = URL.createObjectURL(blob);
          setPreviewUrls((prev) => ({ ...prev, [scannedFile.name]: url }));

          setScanFeedback("Document scanned successfully!");

          // Close scanner after short delay
          setTimeout(() => {
            stopScanner();
          }, 1500);
        }
      },
      "image/jpeg",
      0.95
    ); // High quality JPEG
  };

  const stopScanner = (): void => {
    // Stop all video tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    setIsScanning(false);
    setScanFeedback("");
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Ensure we stop camera when component unmounts
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className="max-w-md mx-auto p-4 bg-white min-h-screen">
      <h1 className="text-2xl font-bold text-center mb-6">Upload Documents</h1>

      <div className="mb-6">
        <div className="flex justify-center mb-4">
          <button
            onClick={triggerFileInput}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-4 py-3 rounded-lg flex items-center justify-center w-full"
          >
            <UploadIcon size={20} className="mr-2" />
            Choose Documents
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
          />
        </div>

        <div className="flex justify-center mb-4 space-x-3">
          <button
            onClick={() => {
              if (
                navigator.mediaDevices &&
                typeof navigator.mediaDevices.getUserMedia === "function"
              ) {
                triggerFileInput();
              }
            }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium px-4 py-3 rounded-lg flex items-center justify-center w-1/2"
          >
            <Camera size={20} className="mr-2" />
            Take Photo
          </button>
          <button
            onClick={startDocumentScanner}
            className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-medium px-4 py-3 rounded-lg flex items-center justify-center w-1/2"
          >
            <FileText size={20} className="mr-2" />
            Scan Document
          </button>
        </div>
      </div>

      {/* Scanner UI */}
      {isScanning && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="relative flex-1 flex flex-col">
            {/* Camera Feed */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Document Outline Helper */}
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
              <div className="border-2 border-white border-dashed w-full h-full max-w-md max-h-64 opacity-70"></div>
            </div>

            {/* Controls */}
            <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center p-4 bg-gradient-to-t from-black to-transparent">
              {scanFeedback && (
                <p className="text-white mb-4 text-center">{scanFeedback}</p>
              )}

              <div className="flex items-center space-x-8">
                <button
                  onClick={stopScanner}
                  className="rounded-full p-3 bg-red-500 text-white"
                >
                  <X size={24} />
                </button>

                <button
                  onClick={captureDocument}
                  className="rounded-full p-5 bg-white"
                >
                  <div className="rounded-full w-8 h-8 bg-blue-500"></div>
                </button>
              </div>
            </div>
          </div>

          {/* Hidden canvas for processing */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">
            Selected Documents ({selectedFiles.length})
          </h2>
          <ul className="space-y-3">
            {selectedFiles.map((file, index) => (
              <li
                key={index}
                className="bg-gray-50 p-3 rounded-lg flex items-center"
              >
                {getFileIcon(file)}
                <div className="ml-3 flex-1 overflow-hidden">
                  <p className="font-medium text-gray-800 truncate">
                    {file.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  onClick={() => removeFile(file.name)}
                  className="ml-2 p-1.5 bg-gray-200 hover:bg-gray-300 rounded-full"
                >
                  <X size={16} />
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6">
            <button
              className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-3 rounded-lg flex items-center justify-center w-full"
              onClick={() => alert("Upload functionality would go here")}
            >
              <Check size={20} className="mr-2" />
              Upload {selectedFiles.length}{" "}
              {selectedFiles.length === 1 ? "File" : "Files"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
